import React, { FC, ReactNode, useCallback, useEffect } from "react";

import { endChat, restartChat, setIsProcessing, showSurvey, toggleMaximise } from "../../features/chatbot/chatbotSlice";

import styles from "./ChatContainer.module.css";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import LiveChatIcon from "../icons/LiveChatIcon";
import RestartIcon from "../icons/RestartIcon";
import EndIcon from "../icons/EndIcon";
import MinimizeIcon from "../icons/MinimizeIcon";
import { getChildFrame, sendMessageToChild } from "../../utils/connector";
import { getAppId, getStorage, resetStorage, setStorage } from "../../utils/helper";
import { setUserQuestion } from "../../features/chatConfig/chatConfigSlice";

const ChatContainer: FC<{ children?: ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();

  // Functions
  const closeWidget = () => dispatch(toggleMaximise());
  const restartConversation = () => {
    resetStorage();
    dispatch(restartChat());
    dispatch(setUserQuestion(""));
    const childFrame = getChildFrame();
    if (childFrame) {
      childFrame.contentWindow?.location.reload();
    }
  };

  // State Selectors
  const { isChildReady, isEnded, isProcessing } = useAppSelector((state) => state.chatbot);
  const { chatStyle, chatTitle, chatBorderEnable, chatShadowEnable } = useAppSelector((state) => state.chatConfig.chatConfig!.config.queue1);
  const { chatMode, jevaMode } = useAppSelector((state) => state.chatConfig);

  const onLiveChatClick = () => {
    sendMessageToChild("Talk to Advisor");
  };

  const onEndChatClick = () => {
    const appId = getAppId();
    const count = getStorage(appId, "end-chat");
    if (count !== "1") {
      sendMessageToChild("End chat");
      setStorage(appId, "end-chat", 1);
    } else {
      resetStorage();
      dispatch(endChat(chatMode));
      dispatch(showSurvey());
    }
  };

  // Conditional Styling
  const style = {
    appChatWindow: {},
    chat_head: {
      backgroundColor: `#${chatStyle.chatLabelBGColor}`,
      color: `#${chatStyle.chatLabelFGColor}`,
      borderBottom: `1px solid #${chatStyle.chatBorder}`,
    },
  };

  if (chatBorderEnable) {
    style.appChatWindow = {
      backgroundColor: chatStyle.chatBGColor,
      borderColor: chatStyle.chatBorder,
    };
  }

  if (chatShadowEnable) {
    style.appChatWindow = {
      ...style.appChatWindow,
      boxShadow: `#${chatStyle.chatShadow} 1px 1px 10px`,
    };
  }

  const handleStorage = useCallback(() => {
    let appId = getAppId();
    const storage: any = getStorage(appId, null);
    dispatch(setIsProcessing(storage?.jeva?.isUserInputDisabled || false));
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [handleStorage]);

  return (
    <>
      <div
        id="appChatWindow"
        style={{
          ...style.appChatWindow,
        }}
        // className={isMaximised ? styles.dBlock : styles.dNone}
      >
        <div className={styles.chat_head} style={style.chat_head}>
          <div className={styles.btn_container}>
            <span className={styles.options} style={{ display: !isProcessing && !isEnded && isChildReady && chatMode !== "VOICE" ? "inline-block" : "none" }} onClick={onLiveChatClick}>
              <LiveChatIcon color={chatStyle.chatLabelFGColor} />
            </span>
            <span className={`${styles.options} ${styles.dInline}`} onClick={restartConversation}>
              <RestartIcon color={chatStyle.chatLabelFGColor} />
            </span>
          </div>
          <span className="chatTitleCt">{chatTitle}</span>
          <div className={styles.btn_container}>
            <span className={styles.options} style={{ display: !isProcessing && !isEnded && isChildReady && chatMode !== "VOICE" ? "inline-block" : "none" }} onClick={onEndChatClick}>
              <EndIcon color={chatStyle.chatLabelFGColor} />
            </span>
            <span className={`${styles.options} ${jevaMode === "video_call" ? "" : styles.dInline}`} onClick={closeWidget}>
              <MinimizeIcon color={chatStyle.chatLabelFGColor} />
            </span>
          </div>
        </div>
      </div>
      {children}
    </>
  );
};

export default ChatContainer;
